import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';

/**
 * A save button that says whether it worked.
 *
 * Every editor had its own answer to this and none of them agreed: the assignment screen kept a
 * "Сохранено." banner, the lesson screen turned its own button label into the receipt, and the
 * course screen saved silently when a field lost focus — so a teacher who typed a title and shut
 * the tab had no way of knowing whether the work had survived. Which is what people mean when
 * they ask for a save button: not the saving, the knowing.
 *
 * So the confirmation is not decoration. It appears only once the server has answered, it names
 * the failure when there is one, and it fades on its own rather than sitting there long enough to
 * be mistaken for the receipt of the next save.
 */

/** Long enough to notice on the way to the next field, short enough not to outlive its truth. */
const CONFIRMATION_MS = 2_500;

export function SaveRow({
  onSave,
  label = 'Сохранить',
  disabled,
  children,
}: {
  /** Resolves when the server has agreed. Whatever it throws is shown as it is. */
  onSave: () => Promise<unknown>;
  label?: string;
  /** Typically "nothing has changed". The button stays visible, so nothing jumps about. */
  disabled?: boolean;
  /** Anything that belongs beside the button: a hint, a second action. */
  children?: ReactNode;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  async function save(): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError('');
    setSaved(false);

    try {
      await onSave();
      if (!alive.current) return;

      setSaved(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (alive.current) setSaved(false);
      }, CONFIRMATION_MS);
    } catch (cause) {
      // Never a silent failure: the whole point of the button is knowing where you stand.
      if (alive.current) setError(cause instanceof Error ? cause.message : 'Не удалось сохранить');
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <div className="saverow">
      <button
        type="button"
        className="btn btn--small"
        disabled={busy || disabled}
        onClick={() => void save()}
      >
        {busy ? 'Сохраняем…' : label}
      </button>

      {/* Polite, not assertive: a screen reader hears it at the next pause rather than mid-word. */}
      <span className="saverow__state" role="status" aria-live="polite">
        {saved ? <span className="saverow__done">✓ Сохранено</span> : null}
        {error ? <span className="saverow__error">{error}</span> : null}
      </span>

      {children}
    </div>
  );
}
